import type {
  AbilityDef,
  AbilityId,
  BoardConfig,
  Cell,
  MergeEvent,
  Pos,
  ResolveFrame,
  ResolveResult,
} from "./types";
import { BOMB_CELL, WILD_CELL, isBoss, isSpecial } from "./types";
import { Rng } from "./rng";
import {
  type Grid,
  allMergeableGroups,
  applyGravity,
  connectedGroup,
  connectedTierGroup,
  createGrid,
  droppableColumns,
  idx,
  inBounds,
  landingRow,
  resultCell,
} from "./board";

/** Meta-growth modifiers (from skill tree / prestige, spec §7). */
export interface EngineModifiers {
  /** Multiplier on all score earned. */
  scoreMult: number;
  /** Multiplier on combo-gauge charge gained. */
  chargeGainMult: number;
  /** Charge the run starts with. */
  startCharge: number;
  /** Ability cost multiplier (<= 1 reduces costs). */
  abilityCostMult: number;
  /** Probability to bump a spawned tier up by one. */
  spawnBias: number;
}

const DEFAULT_MODIFIERS: EngineModifiers = {
  scoreMult: 1,
  chargeGainMult: 1,
  startCharge: 0,
  abilityCostMult: 1,
  spawnBias: 0,
};

export interface EngineOptions {
  config: BoardConfig;
  seed: string;
  /** Points per tier level (index by level; index 0 unused). Falls back to level. */
  tierPoints?: number[];
  /** Number of upcoming pieces to expose in the queue (1..2). */
  previewCount?: number;
  /** Meta-growth modifiers; omitted fields default to no-op. */
  modifiers?: Partial<EngineModifiers>;
}

/** Default minimum same-tier group size to merge (grid play; 2 is trivial). */
export const DEFAULT_MERGE_MIN = 3;

/** Group size at/above which a merge promotes two tiers instead of one. */
export const BIG_MERGE_MIN = 5;

/** Combo-gauge charge granted by a bomb detonation (spec §3.6). */
export const BOMB_CHARGE_GAIN = 8;

/** Find the anchor tier of a group containing wilds (the first non-special cell). */
function anchorTier(grid: Grid, group: Pos[]): number {
  for (const p of group) {
    const v = grid.cells[idx(grid, p.r, p.c)];
    if (!isSpecial(v) && v > 0) return v;
  }
  return 0;
}

/** Cells cleared by a bomb at `center`: the bomb itself + its 3×3 neighborhood. */
function bombArea(grid: Grid, center: Pos): Pos[] {
  const out: Pos[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const r = center.r + dr;
      const c = center.c + dc;
      if (!inBounds(grid, r, c)) continue;
      if (grid.blocked[idx(grid, r, c)]) continue;
      if (grid.cells[idx(grid, r, c)] !== 0 || (dr === 0 && dc === 0)) out.push({ r, c });
    }
  }
  return out;
}

const NEIGHBORS4: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/**
 * Deal 1 damage to a boss cell (HP is encoded in the cell value: -100-hp).
 * Returns true if the boss broke (HP reached 0 → cell cleared).
 */
function damageBoss(grid: Grid, i: number): boolean {
  grid.cells[i] += 1; // -103 → -102 → -101 → -100
  if (grid.cells[i] >= -100) {
    grid.cells[i] = 0;
    return true;
  }
  return false;
}

/** Charge consumed/earned via the combo gauge (spec §3.4 / §3.6). */
export const CHARGE_MAX = 100;
export const ABILITIES: Record<AbilityId, AbilityDef> = {
  hammer: { id: "hammer", cost: 30, targets: 1 },
  swap: { id: "swap", cost: 20, targets: 2 },
  promote: { id: "promote", cost: 50, targets: 1 },
  reroll: { id: "reroll", cost: 12, targets: 0 },
};

function scoreMerge(m: MergeEvent, tierPoints: number[], mergeMin: number): number {
  const base = tierPoints[m.toTier] ?? m.toTier;
  // A minimum-size group = 1x; extra fused cells reward more.
  const sizeBonus = 1 + Math.max(0, m.groupSize - mergeMin) * 0.5;
  const chainMult = Math.pow(2, m.chainLevel - 1); // exponential chain growth (spec §3.4)
  return Math.round(base * sizeBonus * chainMult);
}

/** Charge earned from a single merge: bigger groups and deeper chains charge faster. */
function chargeMerge(m: MergeEvent, mergeMin: number): number {
  // Base 2 per merge so abilities are reachable in a normal run; group size and
  // chain depth accelerate it (deep chains are the intended fast-charge path).
  return 2 + Math.max(0, m.groupSize - mergeMin) + (m.chainLevel >= 2 ? m.chainLevel * 3 : m.chainLevel);
}

function sameCells(a: Cell[], b: Cell[]): boolean {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Resolve the board after a piece lands at `trigger` (or `null` for ability-
 * driven board changes, which skip the trigger step and start from gravity).
 *
 * Order (spec §3.7): (1) trigger-adjacent merge → (2) gravity →
 * (3) repeat (whole-board merge → gravity) until stable.
 *
 * Mutates `grid` in place; returns the aggregate result plus ordered animation
 * frames for step-by-step playback.
 */
export function resolve(
  grid: Grid,
  trigger: Pos | null,
  maxTier: number,
  tierPoints: number[],
  mergeMin: number = DEFAULT_MERGE_MIN,
): ResolveResult {
  const merges: MergeEvent[] = [];
  const frames: ResolveFrame[] = [];
  let score = 0;
  let chargeGain = 0;
  let highestTier = 0;
  let chainLevel = 0;
  let bossesBroken = 0;

  /** Each merge deals 1 damage to every distinct boss adjacent to the group. */
  const damageAdjacentBosses = (group: Pos[]): void => {
    const hit = new Set<number>();
    for (const p of group) {
      for (const [dr, dc] of NEIGHBORS4) {
        const r = p.r + dr;
        const c = p.c + dc;
        if (!inBounds(grid, r, c)) continue;
        const i = idx(grid, r, c);
        if (isBoss(grid.cells[i])) hit.add(i);
      }
    }
    for (const i of hit) {
      if (damageBoss(grid, i)) bossesBroken += 1;
    }
  };

  const snapshot = (kind: ResolveFrame["kind"], stepMerges: MergeEvent[]): void => {
    const cells = grid.cells.slice();
    const prev = frames[frames.length - 1];
    // Collapse no-op frames (e.g. gravity that moved nothing) unless they carry merges.
    if (prev && stepMerges.length === 0 && sameCells(prev.cells, cells)) return;
    frames.push({ cells, kind, chainLevel, merges: stepMerges });
  };

  const promote = (group: Pos[], fromTier: number, result: Pos): void => {
    // Bosses adjacent to the fusing group take 1 damage (mini-boss battles).
    // Evaluated before the group is cleared so adjacency reflects what the
    // player actually saw on the board.
    damageAdjacentBosses(group);
    // Fusing a large group (5+) leaps two tiers instead of one — a big-merge reward.
    const step = group.length >= BIG_MERGE_MIN ? 2 : 1;
    const toTier = Math.min(fromTier + step, maxTier);
    for (const p of group) grid.cells[idx(grid, p.r, p.c)] = 0;
    grid.cells[idx(grid, result.r, result.c)] = toTier;
    const ev: MergeEvent = { fromTier, toTier, groupSize: group.length, result, chainLevel };
    merges.push(ev);
    score += scoreMerge(ev, tierPoints, mergeMin);
    chargeGain += chargeMerge(ev, mergeMin);
    if (toTier > highestTier) highestTier = toTier;
  };

  snapshot("land", []);

  // Step 1: trigger-adjacent merge only.
  if (trigger) {
    const triggerCell = grid.cells[idx(grid, trigger.r, trigger.c)];

    if (triggerCell === BOMB_CELL) {
      // Bomb landing: detonate before any merging. Clears its 3×3 neighborhood
      // (the bomb itself + 8 surrounding cells), then we fall through to the
      // gravity-and-chain loop below so collapsed pieces can still cascade.
      // Bosses in the blast are tough: they take 1 damage instead of clearing.
      const cleared = bombArea(grid, trigger);
      for (const p of cleared) {
        const i = idx(grid, p.r, p.c);
        if (isBoss(grid.cells[i])) {
          if (damageBoss(grid, i)) bossesBroken += 1;
        } else {
          grid.cells[i] = 0;
        }
      }
      chargeGain += BOMB_CHARGE_GAIN;
      const cells = grid.cells.slice();
      frames.push({
        cells,
        kind: "bomb",
        chainLevel: 0,
        merges: [],
        bombCleared: cleared,
        bombCenter: trigger,
      });
    } else if (triggerCell === WILD_CELL) {
      // Wild landing: try each tier neighbor; pick the largest valid component
      // that contains the wild (deterministic by lowest tier on size tie).
      let best: { group: Pos[]; tier: number } | null = null;
      for (const [dr, dc] of [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ] as const) {
        const nr = trigger.r + dr;
        const nc = trigger.c + dc;
        if (!inBounds(grid, nr, nc)) continue;
        const nv = grid.cells[idx(grid, nr, nc)];
        if (nv <= 0 || isSpecial(nv) || nv >= maxTier) continue;
        const group = connectedTierGroup(grid, { r: nr, c: nc }, nv);
        if (group.length < mergeMin) continue;
        if (!best || group.length > best.group.length || (group.length === best.group.length && nv < best.tier)) {
          best = { group, tier: nv };
        }
      }
      if (best) {
        chainLevel = 1;
        const stepMerges: MergeEvent[] = [];
        const before = merges.length;
        promote(best.group, best.tier, resultCell(best.group, trigger));
        stepMerges.push(...merges.slice(before));
        snapshot("merge", stepMerges);
      }
    } else if (triggerCell > 0 && triggerCell < maxTier) {
      const group = connectedGroup(grid, trigger);
      if (group.length >= mergeMin) {
        chainLevel = 1;
        const stepMerges: MergeEvent[] = [];
        const before = merges.length;
        promote(group, triggerCell, resultCell(group, trigger));
        stepMerges.push(...merges.slice(before));
        snapshot("merge", stepMerges);
      }
    }
  }

  // Steps 2+: gravity, then whole-board merge, until stable.
  while (true) {
    applyGravity(grid);
    snapshot("gravity", []);
    const groups = allMergeableGroups(grid, maxTier, mergeMin);
    if (groups.length === 0) break;
    chainLevel += 1;
    const before = merges.length;
    for (const group of groups) {
      // The first cell of a group can be a wild (if it sits at the top-left of
      // a tier+wild blob); look up the actual anchor tier.
      const fromTier = anchorTier(grid, group);
      if (fromTier <= 0) continue;
      promote(group, fromTier, resultCell(group));
    }
    snapshot("merge", merges.slice(before));
  }

  return { merges, maxChain: chainLevel, score, highestTier, chargeGain, bossesBroken, frames };
}

/** Stateful game engine wrapping a grid, RNG, spawn queue, score, and combo gauge. */
export class Engine {
  readonly config: BoardConfig;
  readonly grid: Grid;
  readonly rng: Rng;
  readonly modifiers: EngineModifiers;
  readonly mergeMin: number;
  private readonly tierPoints: number[];
  private readonly previewCount: number;

  /** Upcoming pieces; `queue[0]` is the piece to drop next. */
  queue: Cell[] = [];
  score = 0;
  maxChain = 0;
  drops = 0;
  maxTierReached = 1;
  gameOver = false;
  /** Combo-gauge charge (0..CHARGE_MAX), spent on active abilities. */
  charge = 0;

  constructor(opts: EngineOptions) {
    this.config = opts.config;
    this.grid = createGrid(opts.config);
    this.rng = new Rng(opts.seed);
    this.tierPoints = opts.tierPoints ?? [];
    this.previewCount = Math.min(2, Math.max(1, opts.previewCount ?? 2));
    this.modifiers = { ...DEFAULT_MODIFIERS, ...opts.modifiers };
    this.mergeMin = opts.config.mergeMin ?? DEFAULT_MERGE_MIN;
    this.charge = Math.max(0, Math.min(CHARGE_MAX, Math.round(this.modifiers.startCharge)));
    this.refillQueue();
  }

  private spawnTier(): Cell {
    const key = this.rng.weightedKey(this.config.spawnTable);
    if (key === "wild") return WILD_CELL;
    if (key === "bomb") return BOMB_CELL;
    let tier = Number(key);
    if (!Number.isFinite(tier) || tier <= 0) tier = 1;
    // rich_spawn: chance to bump up a tier. Only draws rng when active, so the
    // default (bias 0) keeps existing deterministic behavior unchanged.
    if (this.modifiers.spawnBias > 0 && this.rng.float() < this.modifiers.spawnBias) {
      tier = Math.min(tier + 1, this.config.maxTier - 1);
    }
    return tier;
  }

  /** Effective ability cost after modifiers. */
  abilityCost(id: AbilityId): number {
    return Math.max(1, Math.round(ABILITIES[id].cost * this.modifiers.abilityCostMult));
  }

  private refillQueue(): void {
    while (this.queue.length < this.previewCount + 1) {
      this.queue.push(this.spawnTier());
    }
  }

  /** The piece that will drop on the next `drop()` call. */
  get current(): Cell {
    return this.queue[0];
  }

  /** Preview of pieces after the current one. */
  get preview(): Cell[] {
    return this.queue.slice(1, 1 + this.previewCount);
  }

  droppableColumns(): number[] {
    return droppableColumns(this.grid);
  }

  private absorb(result: ResolveResult): ResolveResult {
    this.score += Math.round(result.score * this.modifiers.scoreMult);
    this.charge = Math.min(
      CHARGE_MAX,
      this.charge + result.chargeGain * this.modifiers.chargeGainMult,
    );
    if (result.maxChain > this.maxChain) this.maxChain = result.maxChain;
    if (result.highestTier > this.maxTierReached) this.maxTierReached = result.highestTier;
    if (droppableColumns(this.grid).length === 0) this.gameOver = true;
    return result;
  }

  /**
   * Drop the current piece into column `c`. Returns the resolution result,
   * or null if the column cannot accept a piece (invalid move).
   */
  drop(c: number): ResolveResult | null {
    if (this.gameOver) return null;
    const row = landingRow(this.grid, c);
    if (row < 0) return null;

    const tier = this.queue.shift()!;
    this.grid.cells[idx(this.grid, row, c)] = tier;
    this.refillQueue();
    this.drops += 1;

    return this.absorb(resolve(this.grid, { r: row, c }, this.config.maxTier, this.tierPoints, this.mergeMin));
  }

  // ---- Active abilities (spec §3.6) ----

  canUse(id: AbilityId): boolean {
    return !this.gameOver && this.charge >= this.abilityCost(id);
  }

  private spend(id: AbilityId): void {
    this.charge -= this.abilityCost(id);
  }

  private occupied(p: Pos): boolean {
    return (
      inBounds(this.grid, p.r, p.c) &&
      !this.grid.blocked[idx(this.grid, p.r, p.c)] &&
      this.grid.cells[idx(this.grid, p.r, p.c)] !== 0
    );
  }

  /**
   * Hammer: remove one tile, then resolve (the gap may trigger a chain).
   * Bosses are tough — the hammer chips 1 HP instead of removing them.
   */
  useHammer(p: Pos): ResolveResult | null {
    if (!this.canUse("hammer") || !this.occupied(p)) return null;
    this.spend("hammer");
    const i = idx(this.grid, p.r, p.c);
    if (isBoss(this.grid.cells[i])) {
      this.grid.cells[i] += 1; // 1 damage toward -100
      if (this.grid.cells[i] >= -100) this.grid.cells[i] = 0;
    } else {
      this.grid.cells[i] = 0;
    }
    return this.absorb(resolve(this.grid, null, this.config.maxTier, this.tierPoints, this.mergeMin));
  }

  /** Swap: exchange two orthogonally-adjacent occupied tiles, then resolve.
   * Bosses are anchored — they can't be swapped. */
  useSwap(a: Pos, b: Pos): ResolveResult | null {
    if (!this.canUse("swap")) return null;
    const adjacent = Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;
    if (!adjacent || !this.occupied(a) || !this.occupied(b)) return null;
    if (isBoss(this.grid.cells[idx(this.grid, a.r, a.c)])) return null;
    if (isBoss(this.grid.cells[idx(this.grid, b.r, b.c)])) return null;
    this.spend("swap");
    const ia = idx(this.grid, a.r, a.c);
    const ib = idx(this.grid, b.r, b.c);
    [this.grid.cells[ia], this.grid.cells[ib]] = [this.grid.cells[ib], this.grid.cells[ia]];
    return this.absorb(resolve(this.grid, null, this.config.maxTier, this.tierPoints, this.mergeMin));
  }

  /** Promote: bump one tile up a tier, then resolve (prefers that cell as trigger). */
  usePromote(p: Pos): ResolveResult | null {
    if (!this.canUse("promote") || !this.occupied(p)) return null;
    const i = idx(this.grid, p.r, p.c);
    // Only real tiers can be promoted — not wilds, bombs, or bosses.
    if (this.grid.cells[i] <= 0 || this.grid.cells[i] >= this.config.maxTier) return null;
    this.spend("promote");
    this.grid.cells[i] = this.grid.cells[i] + 1;
    return this.absorb(resolve(this.grid, p, this.config.maxTier, this.tierPoints, this.mergeMin));
  }

  /** Reroll: replace the current piece with a freshly spawned one (no resolve). */
  useReroll(): boolean {
    if (!this.canUse("reroll")) return false;
    this.spend("reroll");
    this.queue[0] = this.spawnTier();
    return true;
  }
}
